import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/AuthContext";
import { useCalls } from "@/CallsContext";
import {
  isTrialPopupForced,
  markTrialPopupShownToday,
  wasTrialPopupShownToday,
} from "@/lib/trialPopup";

function hasAccess(license, user) {
  return (
    license?.active === true ||
    license?.has_access === true ||
    user?.has_access === true
  );
}

function trialStatusOf(license, user) {
  return license?.trial_status || user?.trial_status || "";
}

function daysRemainingOf(license, user) {
  const raw = license?.trial_days_remaining ?? user?.trial_days_remaining;
  const days = Number(raw);
  return Number.isFinite(days) ? days : null;
}

export default function TrialNotice() {
  const { user } = useAuth();
  const { license } = useCalls();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState(null);
  const dismissed = useRef(false);

  const storageId = user?.id || user?.email;
  const status = trialStatusOf(license, user);
  const access = hasAccess(license, user);
  const days = daysRemainingOf(license, user);
  const force = isTrialPopupForced();

  useEffect(() => {
    if (dismissed.current || open) return;
    if (!storageId || !status) return;

    if (status === "expired" && !access) {
      setMode("expired");
      setOpen(true);
      return;
    }

    if (status !== "active") return;
    if (!force && wasTrialPopupShownToday(storageId)) return;

    setMode("trial");
    setOpen(true);
  }, [storageId, status, access, force, open]);

  function onContinue() {
    dismissed.current = true;
    if (mode === "trial" && storageId && !force) {
      markTrialPopupShownToday(storageId);
    }
    setOpen(false);
  }

  if (!open || !mode) return null;

  const daysLabel =
    days == null ? "Check your remaining trial time" : `${days} ${days === 1 ? "day" : "days"} remaining`;

  return (
    <div className="mpd-trial-overlay" data-trial-notice={mode}>
      <div
        className="mpd-trial-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="trial-notice-title"
        aria-describedby="trial-notice-copy"
      >
        {mode === "trial" ? (
          <>
            <h2 id="trial-notice-title">{"You're on your free trial"}</h2>
            <p id="trial-notice-copy">{daysLabel}</p>
          </>
        ) : (
          <>
            <h2 id="trial-notice-title">Your free trial has ended</h2>
            <p id="trial-notice-copy">Activate your plan to continue tracking.</p>
          </>
        )}
        <Button type="button" className="mpd-trial-continue" onClick={onContinue}>
          Continue
        </Button>
      </div>
    </div>
  );
}
